// The dashboard, once it is allowed to be seen.
//
// Split from the gate so `useTunerFeed` only exists on the far side of it — a
// hook cannot be called conditionally, so the alternative is an `enabled` flag
// threaded into it, and a socket that opens only to be refused.

import { useEffect, useMemo, useState } from "react";

import { AdminBar, AdminNav, type AdminPage } from "../AdminNav";
import { FeedbackPanel } from "../FeedbackPanel";
import { Btn, Segmented, styled, Text } from "../kit";
import {
  ALL_SERIES,
  computeView,
  feedIsStale,
  filterGallery,
  profilesInGallery,
  pulseView,
  readSeries,
  type SeriesShown,
  since,
  summaryOf
} from "../tunerView";
import { useTunerFeed } from "../useTunerFeed";
import { BoardPanel } from "./BoardPanel";
import { ComputeStrip, Figures, LiveStrip } from "./LiveStrip";
import { ProfileRow } from "./ProfileRow";
import { SeriesToggles } from "./SeriesToggles";
import { TunerGame } from "./TunerGame";
import { TEAM_TONE } from "./palette";
import { Card, ColLabel, Heading, Lamp, Legend, Row, Spacer } from "./parts";

/** Where the chart's line choice is remembered. */
const SERIES_KEY = "chessfactory.tuner.series";

/**
 * The scroller.
 *
 * **Transparent while a board is open**, or the page paints over the very thing
 * it was opened to show — the canvas is fixed behind this element, so an opaque
 * background hides it completely. The cards keep their own panels, so what
 * shows through is the gaps and the free third.
 */
const Screen = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "clear"
})<{ clear?: boolean }>(({ theme, clear }) => ({
  height: "100vh",
  overflowY: "auto",
  position: "relative",
  zIndex: 1,
  background: clear ? "transparent" : theme.kit.color.ground,
  color: theme.kit.color.ink,
  fontFamily: theme.kit.font.body
}));

/**
 * The column that covers the board is the column that takes the pointer, and it
 * has to be one element: `BoardPanel` measures this to inset the camera, and
 * the canvas behind takes whatever the page does not cover — so a boundary
 * measured off something narrower than what is actually laid over the board
 * leaves a strip where the board is drawn and the page eats the drag.
 *
 * Two thirds while a board is open, so the last third is left with nothing over
 * it. Nothing below the wide breakpoint, where the cards already have the
 * window.
 */
const Column = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "narrow"
})<{ narrow?: boolean }>(({ theme, narrow }) => ({
  boxSizing: "border-box",
  maxWidth: narrow ? "none" : 1180,
  margin: narrow ? 0 : "0 auto",
  padding: theme.kit.space.xl,
  [`@media (min-width: ${theme.kit.breakpoint.wide}px)`]: {
    width: narrow ? "66%" : "auto"
  }
}));

const Grid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.kit.space.md,
  gridTemplateColumns: "1fr",
  [`@media (min-width: ${theme.kit.breakpoint.wide}px)`]: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
  }
}));

const Stack = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.md,
  minWidth: 0
}));

const SPACED = { marginBottom: 16 };

/** A block with the page's own gap under it. */
const Spaced = styled("div")(({ theme }) => ({ marginBottom: theme.kit.space.lg }));

export function Dashboard({
  onClose,
  onGo
}: {
  onClose: () => void;
  onGo: (page: AdminPage) => void;
}): React.ReactElement {
  const { digest, pulse, status, receivedAt } = useTunerFeed();
  const [profile, setProfile] = useState<string | null>(null);
  const [winsOnly, setWinsOnly] = useState(false);
  /**
   * Which lines every chart draws.
   *
   * Remembered, because this is a dashboard somebody checks on twice a day and
   * re-picking the view each time is the setting not existing. Read through
   * `readSeries`, which fills a key a stored setting predates — an absent one
   * read as `undefined` is a line that quietly stops being drawn.
   */
  const [series, setSeries] = useState<SeriesShown>(() => {
    try {
      return readSeries(window.localStorage.getItem(SERIES_KEY));
    } catch {
      // Private browsing, or no DOM. A dashboard that cannot remember is still
      // a dashboard.
      return ALL_SERIES;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(SERIES_KEY, JSON.stringify(series));
    } catch {
      // As above.
    }
  }, [series]);
  // One clock for the whole page, so every "3m ago" on screen agrees. Held in
  // state and ticked rather than read per render: `since` is called dozens of
  // times a render, and reading the clock at each call site can print two
  // different answers to the same question on one screen.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(timer);
  }, []);

  // Memoised rather than defaulted inline: `digest?.gallery ?? []` mints a new
  // empty array on every render, so the two hooks below would recompute on
  // every render of a page that is idle most of the time.
  const gallery = useMemo(() => digest?.gallery ?? [], [digest]);
  const shown = useMemo(
    () => filterGallery(gallery, { profile, winsOnly }),
    [gallery, profile, winsOnly]
  );
  /**
   * Which position is on the board at the side.
   *
   * Held as the game's **id** rather than the game itself, because the feed
   * replaces the whole digest every half minute — hold the object and the panel
   * is pinned to a copy that is no longer in the list, and a game that has aged
   * out of the gallery keeps a board open on it forever.
   */
  const [board, setBoard] = useState<{ id: string; frame: "mid" | "final" } | null>(null);
  const showing = useMemo(
    () => (board ? (shown.find((game) => game.id === board.id) ?? null) : null),
    [board, shown]
  );
  // Filtered away, or gone from the feed: close rather than leave an empty
  // column holding the layout open.
  useEffect(() => {
    if (board && !showing) setBoard(null);
  }, [board, showing]);
  const names = useMemo(() => profilesInGallery(gallery), [gallery]);
  const summary = summaryOf(digest);
  const stale = feedIsStale(pulse, digest, now);
  const strip = pulseView(pulse, now);
  const compute = computeView(digest?.compute, pulse, now);

  const live = status === "live" && !stale;
  const lamp = live
    ? TEAM_TONE.candidate
    : status === "connecting"
      ? TEAM_TONE.draw
      : TEAM_TONE.stock;

  return (
    <Screen clear={Boolean(board)}>
      <AdminBar>
        <AdminNav current="tuner" onGo={onGo} />
        <Row>
          <Lamp style={{ background: lamp }} />
          <Text tone="dim" size={12}>
            {live
              ? "live"
              : status === "connecting"
                ? "connecting"
                : stale
                  ? "feed stale"
                  : "offline"}
            {pulse ? ` · heard ${since(pulse.at, now)}` : ""}
            {digest ? ` · built ${since(digest.at, now)}` : ""}
            {receivedAt > 0 && status === "live" ? ` · had ${since(receivedAt, now)}` : ""}
          </Text>
        </Row>
        <Spacer />
        {digest && (
          <Legend
            text={`metrics v${digest.metricsVersion} · planner v${digest.plannerVersion}`}
            title="The measurement basis"
            rule="What the numbers were measured under. METRICS_VERSION moves when the rules move; PLANNER_VERSION when the opponent's code does. A champion measured under either one is not comparable with one measured under another."
          />
        )}
        <Btn variant="ghost" small onClick={onClose}>
          Close
        </Btn>
      </AdminBar>

      <Column data-tuner-cards narrow={Boolean(board)}>
        {strip && (
          <Spaced>
            <LiveStrip strip={strip} stale={stale} />
          </Spaced>
        )}

        {compute && (
          <Spaced>
            <ComputeStrip compute={compute} />
          </Spaced>
        )}

        {!digest && (
          <Card solid>
            <Text block weight={700}>
              {status === "connecting" ? "Connecting…" : "Nothing to show yet"}
            </Text>
            <Text block size={14}>
              {status === "connecting"
                ? "Waiting for the first report from the search."
                : "No results have arrived yet. This fills in on its own once the search reports."}
            </Text>
          </Card>
        )}

        {digest && (
          <>
            <Figures style={SPACED}>
              {(
                [
                  ["profiles", summary.profiles],
                  ["generations", summary.generations],
                  ["games kept", summary.games],
                  ["wins", summary.wins]
                ] as const
              ).map(([label, value]) => (
                <div key={label}>
                  <Text role="display" block weight={700} size={20}>
                    {value}
                  </Text>
                  <ColLabel>{label}</ColLabel>
                </div>
              ))}
            </Figures>

            <Heading>Progress</Heading>
            <Text block tone="dim" size={12} style={SPACED}>
              One chart each, never pooled — a profile is a fitness function, not a competitor, so
              these scores are on different scales and cannot be ranked against each other.
            </Text>

            {/* **One row for every chart, not one per card.** The charts are
                read against each other, so two of them drawing different series
                is a comparison between two different pictures. Turning one off
                takes it out of the *scale* as well as off the canvas, which is
                the point: series sharing an axis flatten whichever has the
                smaller spread into a band across the middle. */}
            <SeriesToggles shown={series} onChange={setSeries} />
            <Grid>
              {digest.profiles.map((entry) => (
                <ProfileRow key={entry.name} profile={entry} now={now} shown={series} />
              ))}
            </Grid>

            {/* **What people said, on the same page as how the army is
                trained.** Under the training overview rather than above it: the
                search is what this page has always been, and a section that
                jumps the queue on the day it is added is a section that has to
                be scrolled past for ever after. */}
            <FeedbackPanel now={now} />

            <Heading>Winning games</Heading>
            <Text block tone="dim" size={12} style={SPACED}>
              Every game a candidate won, and a sample of the ones it lost — a winning factory only
              means something beside one that lost.
            </Text>

            <Row style={SPACED}>
              {names.length > 0 && (
                <Segmented
                  small
                  value={profile ?? ""}
                  options={[
                    { value: "", label: "every profile" },
                    ...names.map((name) => ({ value: name, label: name }))
                  ]}
                  onChange={(next) => setProfile(next === "" ? null : next)}
                />
              )}
              <Btn
                small
                variant={winsOnly ? "primary" : "default"}
                aria-pressed={winsOnly}
                onClick={() => setWinsOnly((on) => !on)}
              >
                wins only
              </Btn>
            </Row>

            {shown.length === 0 ? (
              <Text block tone="dim" size={13}>
                {gallery.length === 0
                  ? "No games recorded yet — the gallery fills as the tuner finishes generations."
                  : "No games match that filter."}
              </Text>
            ) : (
              // The board is drawn edge to edge *behind* the page and the
              // camera is panned into the gap the cards leave — see
              // `BoardPanel`. A column would box a wide board into a third of
              // the screen; this way it has the whole canvas and only has to
              // *appear* in the free part.
              <>
                <Stack>
                  {shown.map((game) => (
                    <TunerGame
                      key={game.id}
                      game={game}
                      now={now}
                      onShow={(frame) => setBoard({ id: game.id, frame })}
                    />
                  ))}
                </Stack>
                {board && showing && (
                  <BoardPanel game={showing} frame={board.frame} onClose={() => setBoard(null)} />
                )}
              </>
            )}
          </>
        )}
      </Column>
    </Screen>
  );
}
